import { CommonModule, KeyValue } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { Observable, Subject, combineLatest } from 'rxjs';
import { AppService } from 'src/app/services/app.service';
import { ReportsService } from '../reports.service';
import { map } from 'rxjs/internal/operators/map';
import { GlobalSpinnerService } from 'src/app/shared/global-spinner/global-spinner.service';
import {
  IActiveCaseReportResult,
  ACTIVE_CASE_SEARCH_RESULT_COLUMNS,
  IActiveCasesRequest,
} from '../reports-models';
import { IPaginatedSearch } from 'src/app/shared/models';
import { finalize, takeUntil, tap } from 'rxjs/operators';
import { MessageToUiService } from 'src/app/services/message-to-ui.service';
import { LazyLoadEvent } from 'primeng/api';
import {
  LocalizationService,
  LocalizedNumberFormat,
} from 'src/app/shared/localization/localization.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { Router } from '@angular/router';
import { Table, TableModule } from 'primeng/table';
import { CustomMultiSelectComponent } from 'src/app/shared/custom-multi-select/custom-multi-select.component';
import { FormsModule } from '@angular/forms';
import { SettingsService } from "src/app/services/settings.service";

@Component({
  selector: 'app-active-cases',
  templateUrl: './active-cases.component.html',
  styleUrls: ['./active-cases.component.scss'],
  imports: [CommonModule, TranslateModule, TableModule, CustomMultiSelectComponent, FormsModule]
})
export class ActiveCasesComponent implements OnInit {
  customerNumber$ = this.appService.customerNumber$;
  customers$: Observable<KeyValue<number, string>[]>;
  selectedCustomers: number[] = [];
  includeReminderCases = false;
  activeCases: IActiveCaseReportResult[];

  numberFormat: LocalizedNumberFormat;
  dateFormat: string;
  numberOfRowsPerPage = 20;
  currentPageNumber = 1;
  totalNumberOfItems = 0;
  sortField: string = null;
  sortAscending: boolean = null;
  columns = [...ACTIVE_CASE_SEARCH_RESULT_COLUMNS];
  totalCases: string;
  userAssignedCustomersCount: number;

  canSearch: boolean;
  showResults: boolean;
  isLoading: boolean;
  languageCode: string;

  @ViewChild('activeCasesTable') activeCasesTable: Table;

  private readonly unsubscribe$ = new Subject();

  constructor(
    private appService: AppService,
    private reportsService: ReportsService,
    private notifications: MessageToUiService,
    private spinnerService: GlobalSpinnerService,
    private localisationService: LocalizationService,
    private translateService: TranslateService,
    private router: Router,
    private settingsService: SettingsService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.spinnerService.show();
    this.initializeCustomerList();
    this.localisationService.settings$.subscribe(
      (_) => (this.numberFormat = _.numberPipeFormat)
    );
    this.localisationService.currentLanguage$.subscribe(
      _ => this.languageCode = _

    )
    this.settingsService.getSettingValueByName('AreRef2Ref3Inlcuded')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((areRef2Ref3Inlcuded) => {
        if (!areRef2Ref3Inlcuded.isTruthy()) {
          this.columns = this.columns.filter(
            col => col.name !== 'customerRef2' && col.name !== 'customerRef3'
          );
        }
      });
    
    this.spinnerService.hide();
  }

  private initializeCustomerList() {
    this.customers$ = this.appService.customers$.pipe(
      tap((_) => {
        this.userAssignedCustomersCount = _.length;
        if (this.userAssignedCustomersCount == 1) { this.selectedCustomers = [_[0].number]; }
      }),
      map((_) => _.map((x) => ({ key: x.number, value: `${x.number} - ${x.name}` })))
    );
  }

  onPageChange(event: LazyLoadEvent) {
    if (event.rows != this.numberOfRowsPerPage) { this.numberOfRowsPerPage = event.rows; }
    this.currentPageNumber =
      event.first == 0 ? 1 : event.first / event.rows + 1;

    const newSortField = this.getSortField(event.sortField);
    const resetSorting = event.sortOrder > 0 && !this.sortAscending
    if (this.sortField === newSortField && resetSorting) {
      this.sortField = "debtorName";
      this.sortAscending = true;
      this.activeCasesTable.sortField = this.sortField;
    }
    else {
      this.sortField = event.sortField;
      this.sortAscending = event.sortOrder > 0
    }

    this.onGenerateReport();
  }

  onGenerateReport() {
    if (this.selectedCustomers?.length == 0) {
      this.notifications.errorKey('Errors.Reports.NoCustomersSelected');
      return false;
    }

    const parameters = this.getPaginatedReportParameters();
    this.isLoading = true;

    this.reportsService.getActiveCasesReport(parameters).pipe(finalize(() => {
      this.isLoading = false;
      this.cdr.detectChanges();
    }))
      .subscribe((
        casesList
      ) => {
        this.activeCases = casesList.pageItems;
        this.translateStatusText(this.languageCode);
        this.totalNumberOfItems = casesList.totalCount;
        this.totalCases = this.translateService.instant('Reports.ActiveCases.TotalCases') + casesList.totalCount.toString();
      });
  }

  private getPaginatedReportParameters(): IPaginatedSearch<IActiveCasesRequest> {
    return {
      currentPage: this.currentPageNumber,
      pageSize: this.numberOfRowsPerPage,
      filter: this.getReportParameters(),
      sortField: this.getSortField(this.sortField),
      sortAscending: this.sortAscending
    } as IPaginatedSearch<IActiveCasesRequest>;
  }

  private getReportParameters(): IActiveCasesRequest {
    const customerNumbers = this.selectedCustomers.join(',');
    const includeReminderCases = this.includeReminderCases;
    
    return {
      customerNumbers,
      includeReminderCases
    } as IActiveCasesRequest;
  }

  public onSelectedCustomersChange(event: number[]) {
    if (event.length == 0) {
      this.activeCases = null;
      this.totalCases = null;
    }
  }

  onExportExcelClick() {

    if (this.selectedCustomers?.length == 0) {
      this.notifications.errorKey('Errors.Reports.NoCustomersSelected');
      return false;
    }
    const parameters = this.getReportParameters();
    this.isLoading = true;
    this.reportsService.exportActiveCasesForExcel(parameters, this.translateService.getDefaultLang())
      .pipe(finalize(() => { this.isLoading = false; }), takeUntil(this.unsubscribe$))
      .subscribe(
        blob => {
          saveAs(blob, `${this.translateService.instant('Reports.ActiveCases')}_${new Date().getDate() + (new Date().getMonth() + 1) + new Date().getFullYear() + '_' + new Date().getTime().toString().slice(0, 6)}.xlsx`);
        },
        error => {
          this.notifications.genericError();
        });
  }


  openCaseInNewTab(caseModel: IActiveCaseReportResult) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/cases/${caseModel.caseNumber}`])
    );
    window.open(url, '_blank');
  }

  private translateStatusText(languageCode) {
    this.activeCases = this.activeCases.map(x => {
      x.statusTextDisplay = languageCode.equalsIgnoringCase('en') ? x.statusTextEn : x.statusText;
      return x;
    });
  }

  private getSortField(sortField: string) {
    if (sortField?.includes('status')) {
      return this.languageCode.equalsIgnoringCase('en') ? "StatusTextEn" : "StatusText"
    }
    return sortField
  }
}
